/**
 * ProfileSuccessFeaturePage — /features/profile-success
 *
 * Public marketing/education page for the LinkedIn Profile Success service.
 * Explains how the audit works with hypothetical visual mockups of:
 *   • the profile overview dashboard (score ring, dimension breakdown)
 *   • the profile rewrite view (Before → After sections)
 * No real user data is referenced — every screenshot on this page is a
 * hand-crafted CSS mockup so we can show product shape without leaking
 * anyone's LinkedIn content.
 */
import React from 'react';
import { AppShell } from '../components/Layout';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Sparkles, CheckCircle2, ArrowRight, TrendingUp, Target, Eye,
  FileText, Zap, ShieldCheck, MessageSquare, Award, Users, ChevronRight,
} from 'lucide-react';

const DIMENSIONS = [
  { key: 'headline', label: 'Headline & positioning', score: 62, color: 'sky' },
  { key: 'about', label: 'About storytelling', score: 71, color: 'emerald' },
  { key: 'experience', label: 'Experience outcomes', score: 58, color: 'amber' },
  { key: 'skills', label: 'Skills & keywords', score: 78, color: 'blue' },
  { key: 'featured', label: 'Featured content', score: 45, color: 'rose' },
];

function ScoreRing({ score = 76 }) {
  const c = 2 * Math.PI * 40;
  const off = c - (c * score) / 100;
  return (
    <div className="relative w-[128px] h-[128px]">
      <svg width="128" height="128" viewBox="0 0 100 100" className="-rotate-90">
        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(148,163,184,0.25)" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="40" fill="none" stroke="rgb(56, 189, 248)" strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[34px] font-bold tabular-nums text-slate-900">{score}</div>
        <div className="text-[9.5px] font-mono-edge tracking-[0.16em] uppercase text-slate-500">Blayz score</div>
      </div>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] overflow-hidden">
      <div className="flex items-center gap-1.5 h-8 border-b border-slate-100 bg-slate-50 px-3">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] text-slate-500 font-mono-edge tracking-wide">blayzrr.com / audit / overview</span>
      </div>
      <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
        <div className="flex flex-col items-center">
          <ScoreRing score={76} />
          <div className="mt-3 flex items-center gap-1.5 text-emerald-600 text-[12px] font-semibold">
            <TrendingUp size={13} /> +18 vs baseline
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Overview</span>
            <span className="text-slate-300">·</span>
            <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-slate-500">Corporate · Recruiter Visibility</span>
          </div>
          <h3 className="mt-1 text-[17px] font-bold text-slate-900 leading-tight">
            5 fixes will move your profile out of the ignore pile.
          </h3>
          <div className="mt-3.5 space-y-2">
            {DIMENSIONS.map((d) => (
              <div key={d.key} className="flex items-center gap-3">
                <div className="text-[12.5px] text-slate-700 w-[180px] shrink-0">{d.label}</div>
                <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full bg-${d.color}-400`} style={{ width: `${d.score}%` }} />
                </div>
                <div className="w-9 text-right text-[12px] font-semibold text-slate-700 tabular-nums">{d.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RewriteMockup() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] overflow-hidden">
      <div className="flex items-center gap-1.5 h-8 border-b border-slate-100 bg-slate-50 px-3">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] text-slate-500 font-mono-edge tracking-wide">blayzrr.com / audit / rewrite → about</span>
      </div>
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
            <div className="flex items-center gap-2 text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-rose-600">
              <FileText size={12} /> Before
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
              Senior product manager with 12+ years of experience in leading cross-functional teams and delivering results across multiple domains.
              Passionate about building great products and driving innovation.
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <div className="flex items-center gap-2 text-[10.5px] font-mono-edge tracking-[0.18em] uppercase text-emerald-600">
              <Sparkles size={12} /> After
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-800">
              I ship products people quietly rely on.<br /><br />
              Three outcomes I stake my career on: took Acme Payments from ₹4 Cr → ₹32 Cr ARR, rebuilt a 4-person onboarding team into a 22-person growth engine,
              and led the mid-market pricing reset that lifted net retention past 120%. Currently open to Head of Product roles in fintech / SaaS.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-[11px] px-2.5 py-1">
            <ShieldCheck size={11} /> No invented metrics
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 text-sky-700 text-[11px] px-2.5 py-1">
            <Target size={11} /> Recruiter keywords preserved
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] px-2.5 py-1">
            <Award size={11} /> 3 outcomes lead the opening
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ProfileSuccessFeaturePage() {
  const navigate = useNavigate();
  return (
    <AppShell>
      <section className="relative overflow-x-clip bg-gradient-to-b from-slate-50 via-white to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-14 lg:pt-16 pb-10 sm:pb-14 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-500/10 dark:text-sky-300 px-3 py-1 text-[11.5px] font-mono-edge tracking-[0.16em] uppercase">
              <Sparkles size={12} /> LinkedIn Profile Success
            </div>
            <h1 className="mt-4 text-[34px] sm:text-[42px] lg:text-[48px] font-semibold tracking-[-0.02em] leading-[1.05] text-foreground" data-testid="profile-success-heading">
              A profile that stops the scroll — audited, scored, and rewritten for the moment recruiters and buyers are deciding.
            </h1>
            <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed max-w-[560px]">
              Upload your current LinkedIn PDF. In under 5 minutes you get a scorecard, a prioritised list of gaps, and a full rewrite of your Headline, About, and Experience — grounded in what your profile actually says today. No invented numbers. No generic language.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate('/')} data-testid="profile-success-cta-primary" className="gap-2">
                <Zap size={16} /> Get my LinkedIn makeover <ArrowRight size={16} />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/pricing')} data-testid="profile-success-cta-pricing">
                See pricing
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-[12.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> Ready in 5 minutes</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> 2 audits of the same profile</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> PDF + DOCX exports</span>
            </div>
          </div>
          <div className="relative">
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="max-w-3xl">
          <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">How it works</p>
          <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight text-foreground">
            Every recommendation is anchored to a line in your profile.
          </h2>
          <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed">
            We upload your LinkedIn PDF, extract every section, and score you against the same recruiter benchmark hiring managers rely on. Then we rewrite each section using only what your profile can already back up — with proof-to-collect notes for the numbers we can&apos;t verify.
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: FileText, title: '1 · Upload your profile PDF', body: 'Export from LinkedIn (Resources → Save to PDF) and drop it in. Nothing is scraped without your consent.' },
            { icon: Target, title: '2 · Pick stage + goal', body: 'Student, fresher or corporate — recruiter visibility, business development or thought leadership. Scoring is calibrated to your combination.' },
            { icon: Sparkles, title: '3 · Read your rewrites', body: '5-tab dashboard: Overview, Scorecard, Rewrite, Plan, Exports. Regenerate any section with a different tone or lens.' },
          ].map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-background p-5">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
                <s.icon size={18} />
              </div>
              <p className="mt-4 text-[15px] font-semibold text-foreground">{s.title}</p>
              <p className="mt-1.5 text-[13.5px] text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rewrite preview */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-16">
        <div className="max-w-3xl mb-6">
          <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-emerald-600">Before → after</p>
          <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight text-foreground">
            The rewrite reads like you at your best, on your best day.
          </h2>
          <p className="mt-3 text-[14.5px] text-muted-foreground leading-relaxed">
            Corporate profiles get named-outcome About openings and top-3 wins that dominate the first screen. Students and freshers get momentum-led trajectory copy. The recruiter lens toggle lets you flip between the two on demand.
          </p>
        </div>
        <RewriteMockup />
      </section>

      {/* What's included */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 items-start">
            <div>
              <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">What&apos;s inside</p>
              <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight text-foreground">
                Everything a hiring manager wants to see, without the AI slop.
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  { icon: Award, text: 'Scorecard with 5-8 dimensions rated 0-100 against your target stage + goal.' },
                  { icon: Eye, text: 'Recruiter-visibility keywords surfaced in Headline, About opening and top-3 Skills.' },
                  { icon: MessageSquare, text: 'About rewrite with 3-outcome lead (corporate) or trajectory lead (fresher/student).' },
                  { icon: Users, text: 'Experience bullets refactored into outcome-led lines — never duty-only.' },
                  { icon: ShieldCheck, text: 'Anti-fabrication guardrails: any number without evidence is flagged as proof-to-collect, never fabricated.' },
                  { icon: FileText, text: 'One-click exports as PDF or DOCX, ready to paste into LinkedIn.' },
                ].map((f) => (
                  <li key={f.text} className="flex items-start gap-3 text-[14px] text-foreground/90 leading-relaxed">
                    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300 shrink-0">
                      <f.icon size={13} />
                    </span>
                    <span>{f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-background p-6">
              <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Starts at</p>
              <p className="mt-1 text-[38px] font-bold text-foreground leading-none">₹199 <span className="text-[14px] font-medium text-muted-foreground">/ $9</span></p>
              <p className="mt-2 text-[13px] text-muted-foreground">One-time, 15-day validity. 2 audits of the same profile.</p>
              <Button size="lg" className="mt-5 w-full gap-2" onClick={() => navigate('/pricing')} data-testid="profile-success-plan-cta">
                See all plans <ChevronRight size={16} />
              </Button>
              <p className="mt-3 text-[11.5px] text-muted-foreground text-center">
                Prefer a human-audited rewrite in 4 hours? See our <Link to="/features/thought-leadership" className="underline text-sky-600">Expert audit</Link> tier on the pricing page.
              </p>
            </div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
